// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PrintingUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import PrintConstants from 'logic/data/Izenda.PrintConstants';
import classnames from 'classnames';
import uiState from 'logic/Izenda.UiState';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import constants from 'logic/data/Izenda.ReportsConstants';
import { isNil, find, map, memoize, replace, filter, reduce } from 'lodash/fp';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import DateTimeUtils from 'share/utils/Izenda.DateTimeUtils';

function getNativeStyle({ height, display, margin }) {
  return `height: ${height}; position: relative; display: ${display}; margin: ${margin}`;
}

function getBbox(reportParts, normalized) {
  // calc report parts bbox
  const bbox = reduce((acc, part) => ({
    x: (isNil(acc.x) ? part.positionX : Math.min(part.positionX, acc.x)),
    y: (isNil(acc.y) ? part.positionY : Math.min(part.positionY, acc.y)),
    x2: (isNil(acc.x2) ? part.positionX + part.width : Math.max(part.positionX + part.width, acc.x2)),
    y2: (isNil(acc.y2) ? part.positionY + part.height : Math.max(part.positionY + part.height, acc.y2))
  }), {
    x: null,
    y: null,
    x2: null,
    y2: null
  }, reportParts || []);
  if (normalized) {
    bbox.x2 -= bbox.x;
    bbox.y2 -= bbox.y;
    bbox.x = 0;
    bbox.y = 0;
  }
  return bbox;
}

export default {
  /**
   * get config style for ReportPart container
   * @param  {any} props
   * @return {any} style object
   */
  getReportPartContainerStyle(props: any) {
    // we only build style for preview of export format setting.
    if (!props.setting || !props.exportFormatSetting) {
      return null;
    }

    const exportFormatSetting = props.exportFormatSetting || {};
    const centerOnPage = exportFormatSetting.centerOnPage;
    return {
      display: 'table',
      margin: centerOnPage.horizontally ? 'auto' : 0,
      height: 'auto'
    };
  },

  /**
   * get config style for ReportPart separator
   * @param  {any} props
   * @param  {boolean} exportFormatSetting
   * @return {any} style object
   */
  getSeparatorContainerStyle(props: any, convertToNativeStyle: boolean) {
    // we only build style for preview of export format setting.
    if (!props.setting || !props.exportFormatSetting) {
      return null;
    }

    const exportFormatSetting = props.exportFormatSetting || {};
    const centerOnPage = exportFormatSetting.centerOnPage;
    const pageSize = PrintConstants.getPageSize(exportFormatSetting.pageSizeKey, exportFormatSetting.orientation);
    const usePageBreakAfterSeparator = props.reportPart.reportPartContent.checkBreakPage();

    const styleObject =
      usePageBreakAfterSeparator && pageSize
        ? {
          height: centerOnPage.vertically ? PrintConstants.formatCSSValueInInches(pageSize.heightInInches - 0.8) : 0,
          display: 'table',
          margin: centerOnPage.horizontally ? 'auto' : 0
        }
        : {};
    return convertToNativeStyle ? getNativeStyle(styleObject) : styleObject;
  },

  /**
   * get Report Part className
   * @param  {any} props
   * @return {any} className object
   */
  getReportPartClassName(props: any) {
    // we only build style for preview of export format setting.
    if (!props.setting || !props.exportFormatSetting) {
      return null;
    }

    const exportFormatSetting = props.exportFormatSetting;
    const centerOnPage = exportFormatSetting ? exportFormatSetting.centerOnPage : {};
    const shouldAlignMiddle = centerOnPage.vertically && !props.reportPart.reportPartContent.checkBreakPage();

    return classnames('u_tableCell', {
      u_alignMiddle: shouldAlignMiddle
    });
  },

  /**
   * get separator className
   * @param  {any} props
   * @return {any} className object
   */
  getSeparatorClassName(props: any) {
    // we only build style for preview of export format setting.
    if (!props.setting || !props.exportFormatSetting) {
      return null;
    }

    const exportFormatSetting = props.exportFormatSetting;
    const centerOnPage = exportFormatSetting ? exportFormatSetting.centerOnPage : {};
    const shouldAlignMiddle = centerOnPage.vertically && props.reportPart.reportPartContent.checkBreakPage();

    return classnames('u_tableCell', {
      u_alignMiddle: shouldAlignMiddle
    });
  },

  /**
   * replace style & class for Form
   * @param  {any} props
   * @param  {string} content
   * @return {string} replaced content
   */
  replaceFormStyles(props: any, content: string) {
    if (!props.printingMode) {
      return null;
    }

    const style = this.getSeparatorContainerStyle(props, true);
    const classes = this.getSeparatorClassName(props);
    const replaceString = `<div style=${style}><div class="${classes}">`;

    content = replace('<div>', replaceString, content);
    content = replace('</div>', '</div></div>', content);
    return content;
  },

  /**
   * Create a singleton render promise of part.
   * @param  {string} partId
   */
  createPartRenderPromise(partId) {
    const newPromise = memoize(() => {
      let resolvePromise = null;
      const renderPromise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
      });
      return {
        partId: partId,
        promise: renderPromise,
        resolve: resolvePromise
      };
    });

    return newPromise();
  },

  /**
   * Resolve value for system variable.
   * @param  {any} store
   * @param  {string} value
   */
  replaceSystemVariable(store: any, item: string) {
    let value = item.value;
    const reportStore = store.reportStore || {};
    const reportModel = reportStore.model || {};

    switch (value) {
      case constants.SYSTEM_VARIABLES.REPORT_NAME:
        value = reportModel.reportName;
        break;
      case constants.SYSTEM_VARIABLES.CURRENT_DATETIME:
        const format = item.format || uiState.currentUser.dateFormat;
        const currentDate = DateTimeUtils.getUtcTimeWithTimestampOffset();
        value = convertToDateTimeFormat(format, currentDate);
        break;
      case constants.SYSTEM_VARIABLES.CURRENT_USERNAME:
        value = uiState.currentUser.fullName;
        break;
      case constants.SYSTEM_VARIABLES.TENANT_NAME:
        value = uiState.currentUser.tenantName;
        break;
    }

    return value;
  },

  getPageMarginsInPercent(pageSizeKey, orientation, pageMargins) {
    const lineSize = PrintConstants.MIN_SIZE_UI_SETTING / 2;
    const pageSize = PrintConstants.getPageSize(pageSizeKey, orientation);

    const line = 100 * lineSize / pageSize.widthInInches;
    const hLeft = 100 * pageMargins.left / pageSize.widthInInches;
    const hRight = 100 * pageMargins.right / pageSize.widthInInches;
    const vTop = 100 * pageMargins.top / pageSize.heightInInches;
    const vBottom = 100 * pageMargins.bottom / pageSize.heightInInches;
    return {
      line: line,
      horizontal: {
        left: hLeft,
        right: hRight,
        center: 100 - Math.round(hLeft + hRight + line * 2)
      },
      vertical: {
        top: vTop,
        bottom: vBottom,
        center: 100 - Math.round(vTop + vBottom + line * 2)
      }
    };
  },

  /**
   * Get current DPI.
   */
  getDPI() {
    // REF: https://github.com/ryanve/res
    const one = {
      dpi: 96
    };

    // Get resolution in dppx on IE.
    function ie() {
      return Math.sqrt(screen.deviceXDPI * screen.deviceYDPI) / one.dpi;
    }

    // Get resolution in dppx
    function dppx() {
      // devicePixelRatio: Webkit (Chrome/Android/Safari), Opera (Presto 2.8+), FF 18+
      return typeof window === 'undefined' ? 0 : +window.devicePixelRatio || ie() || 0;
    }

    return dppx() * one.dpi;
  },

  getBbox(reportParts: any[], normalized: boolean) {
    return getBbox(reportParts, normalized);
  },

  buildScaleStyleSimple(scaleX: number, scaleY: number) {
    const scale = isNil(scaleY) ? `scale(${scaleX})` : `scale(${scaleX}, ${scaleY})`;
    const transformOrigin = `0 0 0`;
    return {
      transformOrigin: transformOrigin,
      MozTransformOrigin: transformOrigin,
      MsTransformOrigin: transformOrigin,
      WebkitTransformOrigin: transformOrigin,
      OTransformOrigin: transformOrigin,
      transform: scale,
      MozTransform: scale,
      MsTransform: scale,
      WebkitTransform: scale,
      OTransform: scale
    };
  },

  /**
   * Compose css style for transform scale.
   * @param  {number} scaleX
   * @param  {number} scaleY
   *
   */
  buildScaleStyle(scaleX: number, scaleY: number, options: any) {
    let transformLeft = '0';
    let transformTop = '0';
    if (options) {
      let partsToRender = options.partsToRender;
      let width = options.width;
      let height = options.height;
      let vertically = options.vertically;
      let horizontally = options.horizontally;
      let isPageBreak = options.isPageBreak;

      // calc report parts bbox
      const bbox = getBbox(partsToRender, false);

      const gridSizeScaledPx = width / 12;
      if (horizontally) {
        const bboxWidth = bbox.x2 - bbox.x;
        const leftShift = 6 - bbox.x - bboxWidth / 2;
        transformLeft = (leftShift * gridSizeScaledPx) + 'px';
      }
      if (vertically) {
        const bboxHeight = bbox.y2 - bbox.y;
        if (bboxHeight * gridSizeScaledPx < height) {
          const topShift = ((0.5 * height) / gridSizeScaledPx) - bbox.y - bboxHeight / 2;
          transformTop = (topShift * gridSizeScaledPx) + 'px';
        }
      }
    }

    const scale = isNil(scaleY) ? `scale(${scaleX})` : `scale(${scaleX}, ${scaleY})`;
    const transformOrigin = `0 0 0`;

    return {
      transformOrigin: transformOrigin,
      MozTransformOrigin: transformOrigin,
      MsTransformOrigin: transformOrigin,
      WebkitTransformOrigin: transformOrigin,
      OTransformOrigin: transformOrigin,
      transform: scale,
      MozTransform: scale,
      MsTransform: scale,
      WebkitTransform: scale,
      OTransform: scale
    };
  },

  /**
   * Get render listener by id.
   * @param  {Array<any>} renderListeners
   * @param  {string} id
   */
  getRenderListenerById(renderListeners: Array<any>, id: string) {
    return find(listener => listener.partId === id, renderListeners);
  },

  /**
   * Get finished render promise by id.
   * @param  {Array<any>} renderListeners
   * @param  {string} id
   */
  getFinishedRenderPromiseById(renderListeners: Array<any>, id: string) {
    const renderListener = this.getRenderListenerById(renderListeners, id);
    return renderListener && renderListener.resolve;
  },

  /**
   * Push render listener into directory by id
   * @param  {Array<any>} renderListeners
   * @param  {string} id
   */
  pushRenderListener(renderListeners: Array<any>, id: string) {
    const renderListener = find(item => item.partId === id, renderListeners);
    !renderListener && renderListeners.push(this.createPartRenderPromise(id));
    return renderListeners;
  },

  /**
   * publish finished render event
   * @param  {Array<any>} renderListeners
   * @param  {any} callback
   */
  publishFinishedRender(renderListeners: Array<any>, callback: any) {
    const childPromises = map(item => item.promise, renderListeners);

    Promise.all(childPromises).then(() => {
      callback && callback();
    });
  },

  /**
   * Print html. This is intended to print html from API.
   * @param {string} content
   */
  printHtml(content: string) {
    const printTab = BrowserUtils.openNewTab();
    if (!printTab) return;
    const doc = printTab.document;
    const printIntervalMs = 100;
    printTab.document.write(content);
    printTab.document.close();
    const print = printInterval => {
      printTab.print();
      printInterval && clearInterval(printInterval);
    };

    printTab.document.body.onload = () => {
      const cssnum = doc.styleSheets.length;
      const printInterval = setInterval(() => {
        const cssRuleSets = filter(s => {
          try {
            return !!s.cssRules;
          } catch (err) {
            return true;
          }
        }, doc.styleSheets);
        cssRuleSets.length === cssnum && printTab.status === 'completed' && print(printInterval);
      }, printIntervalMs);
    };
  }
};



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.PrintingUtils.js